// ─────────────────────────────────────────────
// KLOTHIQ — useTasks hook
// ─────────────────────────────────────────────
// Queries and mutations for tasks, comments, activity,
// and in-app notifications.
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeWorkTask, type WorkTask } from '@klothiq/domain';
import { workRpc } from '@/lib/work-planning';

export const MY_TASKS_KEY = 'my_tasks';
export const TASK_COMMENTS_KEY = 'task_comments';
export const TASK_ACTIVITY_KEY = 'task_activity';
export const NOTIFICATIONS_KEY = 'in_app_notifications';

export interface MyTaskItem extends WorkTask {
  plan_title: string;
  plan_target_date: string | null;
  plan_priority: string;
}

export interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  author_name?: string;
  author_role?: string;
  comment_text: string;
  created_at: string;
}

export interface TaskActivityLog {
  id: string;
  plan_id: string;
  task_id: string | null;
  actor_id: string;
  actor_name?: string;
  actor_role?: string;
  action: string;
  old_values: any;
  new_values: any;
  reason?: string | null;
  created_at: string;
}

export interface InAppNotificationItem {
  id: string;
  organization_id: string;
  recipient_id: string;
  actor_id: string | null;
  actor_name?: string | null;
  title: string;
  message: string;
  link: string | null;
  notification_type: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

// ── Query: My Assigned Tasks ─────────────────────────────────
export function useMyTasks(filters?: { status?: string }) {
  const { profile, can, access } = useAuth();

  return useQuery({
    queryKey: [MY_TASKS_KEY, profile?.id, access?.access_version, filters],
    queryFn: async (): Promise<MyTaskItem[]> => {
      const data = await workRpc<MyTaskItem[]>('get_my_work_tasks_v2', {
        p_filters: filters?.status && filters.status !== 'all' ? { status: filters.status } : {},
      });
      return (data ?? []).map((task) => ({ ...task, ...normalizeWorkTask(task) }));
    },
    enabled: !!profile && can('my_tasks.view'),
    staleTime: 1000 * 15,
    refetchInterval: 30000,
  });
}

// ── Query: Task Comments ──────────────────────────────────────
export function useTaskComments(taskId: string | undefined) {
  return useQuery({
    queryKey: [TASK_COMMENTS_KEY, taskId],
    queryFn: async (): Promise<TaskComment[]> => {
      const { data, error } = await supabase.rpc('get_task_comments', { p_task_id: taskId });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!taskId,
    staleTime: 1000 * 10,
  });
}
export function useTaskActivity(taskId?: string | null, planId?: string | null) {
  return useQuery({
    queryKey: [TASK_ACTIVITY_KEY, taskId || planId],
    queryFn: async (): Promise<TaskActivityLog[]> => {
      const { data, error } = await supabase.rpc('get_task_activity', {
        p_task_id: taskId ?? null,
        p_plan_id: planId ?? null,
      });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!taskId || !!planId,
    staleTime: 1000 * 15,
  });
}

// ── Query: In-App Notifications ──────────────────────────────
export function useInAppNotifications() {
  const { profile, access } = useAuth();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!profile || !access?.ready) return;
    const channel = supabase
      .channel(`notifications:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'in_app_notifications',
          filter: `recipient_id=eq.${profile.id}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY, profile.id] });
          void queryClient.invalidateQueries({ queryKey: [MY_TASKS_KEY, profile.id] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [profile?.id, access?.ready, queryClient]);

  return useQuery({
    queryKey: [NOTIFICATIONS_KEY, profile?.id, access?.access_version],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('in_app_notifications')
        .select(
          `
          id,
          organization_id,
          recipient_id,
          actor_id,
          title,
          message,
          link,
          notification_type,
          is_read,
          read_at,
          created_at
        `,
        )
        .eq('recipient_id', profile!.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const notifications: InAppNotificationItem[] = (data || []).map((row: any) => ({
        id: row.id,
        organization_id: row.organization_id,
        recipient_id: row.recipient_id,
        actor_id: row.actor_id,
        actor_name: row.profiles?.full_name,
        title: row.title,
        message: row.message,
        link: row.link,
        notification_type: row.notification_type,
        is_read: row.is_read,
        read_at: row.read_at,
        created_at: row.created_at,
      }));

      const { count, error: countError } = await supabase
        .from('in_app_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', profile!.id)
        .eq('is_read', false);
      if (countError) throw countError;
      const unreadCount = count ?? 0;

      return { notifications, unreadCount };
    },
    enabled: !!profile && !!access?.ready,
    staleTime: 1000 * 15,
    refetchInterval: 1000 * 30, // poll every 30s for notifications
  });
}

export {
  useCreateTask,
  useUpdateTask,
  useReassignTask,
  useUpdateTaskProgressStatus,
  useAddTaskComment,
} from './useWorkMutations';

// ── Mutation: Mark Notification Read ──────────────────────────
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data, error } = await supabase.rpc('mark_notification_read', {
        p_id: notificationId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
}

// ── Mutation: Mark All Notifications Read ─────────────────────
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_all_notifications_read');

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
}
